import formatPrice from '@dc/ui-library/src/helpers/formatPrice';
import dayjs from 'dayjs';
import { removeHtml } from '../../components/react/utils/strings';
import * as UILocale from '../../utils/UILocale';

export default function normalizeSponsoredProducts(products) {
  // get value of criteo cookie
  // if the cookie is defined, it's later added as a url parameter 'puserid' at the end of
  // onLoadBeacon property and at the end of every
  // product's link, beacons.onView beacons.onClick properties
  const criteoCookieValue = getCriteoCookieValue();

  if (products.onLoadBeacon && products.items) {
    return {
      onLoadBeacon: criteoCookieValue
        ? `${products.onLoadBeacon}&puserid=${criteoCookieValue}`
        : products.onLoadBeacon,
      items: products.items.slice(0, 4).map((item) => normalizeItem(item, criteoCookieValue))
    };
  }

  return null;
}

function getCriteoCookieValue() {
  const criteoCookieRegexResult = document.cookie.match('(^|;) ?criteo=([^;]*)(;|$)');

  return criteoCookieRegexResult ? criteoCookieRegexResult[2] : '';
}

function normalizeItem(item, criteoCookieValue) {
  return {
    id: item.id,
    brand: normalizeBrand(item.brand),
    title: item.label,
    link: `${normalizeLink(item.link)}${criteoCookieValue ? `&puserid=${criteoCookieValue}` : ''}`,
    image: normalizeImage(item.images || []),
    icons: item.icons.map((icon) => ({
      imgUrl: icon.imageUrl,
      ...(icon.link && { linkUrl: icon.link })
    })),
    price: normalizePrice(item.price, item.wasPrice),
    promoMessages: {
      isOnlineOnly: false,
      badges: normalizeBadges(item.badges || [])
    },
    hasOurExpertsLoveTag: false,
    isOutOfStock: false,
    isFlashSale: false,
    channelsAvailability: normalizeChannelsAvailability(item.deliveryOptions),
    descriptionShortened: normalizeDescription(item.fullDescription),
    descriptionBullets: item.mainFeatures || [],
    review: normalizeReview(item.customerReview),
    beacons: normalizeBeaconsForProduct(item.beacons, criteoCookieValue)
  };
}

function normalizeBrand(brand) {
  const title = brand && brand.label ? brand.label : '';
  return { title };
}

function normalizeLink(link) {
  return link ? link : '#';
}

function normalizeReview(customerReview) {
  if (customerReview && customerReview.number && customerReview.averageScore) {
    return {
      reviewCount: customerReview.number,
      score: customerReview.averageScore
    };
  }

  return undefined;
}

function normalizeBeaconsForProduct(beacons, criteoCookieValue) {
  const convertedBeacons = {};

  if (beacons.onClick) {
    convertedBeacons.onClick = criteoCookieValue
      ? `${beacons.onClick}&puserid=${criteoCookieValue}`
      : beacons.onClick;
  }

  if (beacons.onView) {
    convertedBeacons.onView = criteoCookieValue
      ? `${beacons.onView}&puserid=${criteoCookieValue}`
      : beacons.onView;
  }

  return convertedBeacons;
}

function normalizePrice(price, wasPrice) {
  const formattedPrice = formatPrice(price.amount, true, price.currencyCode);
  const formattedWasPrice = wasPrice &&
    formatPrice(wasPrice.amount, true, wasPrice.currencyCode);
  const formattedSavingsPrice = wasPrice &&
    formatPrice(wasPrice.discountAmount, true, wasPrice.currencyCode);

  return {
    price: formattedPrice,
    wasPrice: formattedWasPrice,
    savingsPrice: formattedSavingsPrice,
    wasPriceFrom: wasPrice && wasPrice.dateFrom && formatDate(wasPrice.dateFrom),
    wasPriceTo: wasPrice && wasPrice.dateTo && formatDate(wasPrice.dateTo)
  };
}

function formatDate(date) {
  return dayjs(date).format('DD/MM/YYYY');
}

function normalizeDescription(description) {
  return `${removeHtml(description).slice(0, 250)}...`;
}

function normalizeChannelsAvailability(deliveryOptions) {
  if (deliveryOptions) {
    const delivery = UILocale.getString('component.hooklogic.delivery');
    const inStore = UILocale.getString('component.hooklogic.inStore');
    return {
      defaultAvailability: {
        showEmailMeBack: false,
        canBeDownloaded: isCanBeDownloaded(getDeliveryOption(22, deliveryOptions)),
        delivery: normalizeDelivery(getDeliveryOption(1, deliveryOptions,
          delivery === 'delivery' ? null : delivery)),
        inStore: normalizeDelivery(getDeliveryOption(20, deliveryOptions,
          inStore === 'in_store' ? null : inStore))
      }
    };
  }

  return undefined;
}

function normalizeImage(images) {
  const image = images.length > 0 ? images[0] : null;
  if (image && image.url) {
    const matches = image.url.match(/^(.*)\/(u_.*\.*)$/);
    if (matches && matches.length >= 2) {
      const imageUrl = matches[1];
      const imageName = matches[2];

      return {
        src: `${imageUrl}/${imageName.replace('u_', 'g_')}`,
        label: ''
      };
    }
  }

  return {
    src: '',
    label: ''
  };
}

function isCanBeDownloaded(deliveryOption) {
  return deliveryOption && deliveryOption.enabled;
}

function normalizeDelivery(deliveryOption) {
  const deliveryType = deliveryOption.id && getDeliveryType(deliveryOption.id);
  return deliveryOption && deliveryOption.enabled
    ? {
      type: deliveryType,
      label: deliveryOption.label
    }
    : undefined;
}

function normalizeBadges(badges) {
  return badges.map((item) => ({
    src: item.imageUrl,
    label: item.name
  }));
}

function getDeliveryType(id) {
  switch (id) {
    case 1:
      return 'homeDeliveryAvailable';
    case 20:
      return 'collectInStoreAvailable';
    default:
      return null;
  }

  // 'collectInStoreAvailable',
  //   'homeDeliveryAvailable',
  //   'supplierDeliveryAvailable',
  //   'orderNowDeliveryLaterAvailable'
}

function getDeliveryOption(id, deliveryOptions, translateLabel = null) {
  const deliveryOption = deliveryOptions.find((item) => Number(item.id) === id);
  if (deliveryOption) {
    return {
      ...deliveryOption,
      id: Number(deliveryOption.id),
      label: translateLabel || deliveryOption.label
    };
  }

  return deliveryOption;
}
